import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Copy, X, Pencil, Trash2 } from "lucide-react";
import { Tooltip } from "@mui/material";
import {
  classifyPaste,
  displaySpeaker,
  flipBubbleSpeaker,
  isHostSideSpeaker,
  normalizeBubbles,
  parseTranscript,
  parseTranscriptLines,
  refindLine,
  runAround,
  setHostSpeakerNames,
  serializeEditor,
  serializeRange,
  storedLineRef,
  turnsToHtml,
  type BubbleOptions,
} from "./transcriptDocument";
import {
  fetchTranscriptUtterances,
  TranscriptChangedError,
  type TranscriptLineRef,
} from "../Dashboard/api";
import { formatTranscriptTimestamp } from "./transcriptTimestamp";
import { startsTranscriptGroup } from "./transcriptGrouping";
import { useFlash } from "@/hooks/productTimerHooks";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import SpeakerRenamePicker from "./SpeakerRenamePicker";
import SpeakerMovePicker from "./SpeakerMovePicker";
import SpeakerLineNavigator from "./SpeakerLineNavigator";
import UnidentifiedSpeakers from "./UnidentifiedSpeakers";
import { unidentifiedSpeakers } from "./findUnidentifiedSpeakers";
import {
  useConfirmTranscriptSpeakers,
  useTranscriptSpeakers,
} from "../Data/interactions";
import { userMessageOf } from "@/services/apiError";

export interface TranscriptModalProps {
  /** Interaction public id, used to fetch per-utterance timing (#10002). */
  interactionId: string;
  /** The transcript text, formatted as `Speaker: text` lines. */
  transcript: string;
  /** Whether the current user may edit/remove the transcript. Editing is
   *  offered only when `onSave` and `onDelete` are given too. */
  canEdit: boolean;
  /**
   * A passage to scroll to and mark on open — a notetaker chat citation to
   * this transcript (#11205). Matched as a substring of one line's text.
   */
  highlightText?: string;
  /** Which of the lines holding `highlightText` to land on, counting from 0;
   *  the first when absent (#12375). */
  highlightOccurrence?: number;
  onClose: () => void;
  /** Persist an edited transcript. An empty string removes it. Absent in a
   *  read-only host (the side viewer). */
  onSave?: (transcript: string) => Promise<void>;
  /** Remove the transcript entirely. Absent in a read-only host. */
  onDelete?: () => Promise<void>;
  /**
   * Rename every line of one speaker (#11884); `userId` when the new name is
   * a colleague, `entityId` when it is a person record (#14412). Without it,
   * speaker labels are plain text.
   */
  onRenameSpeaker?: (
    label: string,
    name: string,
    userId?: string,
    entityId?: string
  ) => Promise<void>;
  /**
   * Move one line, or a run of them, to another of the transcript's voices
   * (#13917): each line by its index in the stored text with the label and
   * text it carries there, to `speaker`, a label the text carries, or to a
   * new placeholder voice for null. Without it, bubbles do nothing.
   */
  onReassignSpeaker?: (
    lines: TranscriptLineRef[],
    speaker: string | null
  ) => Promise<void>;
}

/** The speaker panel (#12489, #13917): the speaker whose lines the reader
 * goes through, which of them is current, and how much of the current
 * line's run a move takes. The panel names the speaker (every line) and
 * moves the current line, or its run, to another voice. */
type Chosen = {
  label: string;
  at: number;
  scope: "line" | "run";
  /** The current line as stored when the panel last went to it: a
   * transcript that changes that line lets the panel go. */
  anchor?: StoredLine;
  /** A move refused as stale (409): the transcript it was refused against,
   * the line, and its run then (its stored lines). The panel stays at the
   * line, saying why, until the next transcript, which finds the line again
   * by its text. */
  held?: StoredLine & { against: string; run: string[] };
  /** A move landed: the line to go on to (the speaker's next one, or the
   * one before; none when it was their last), found by its text in the
   * transcript that follows. */
  follow?: { against: string; to: StoredLine | null };
};

/** A line of the stored text: where it is, and what it read. */
type StoredLine = { line: number; raw: string };

// Scoped styles for the editable bubble document. The speaker label is a CSS
// ::before pseudo-element so it never enters the editable text or textContent —
// the editor serializes cleanly back to `Speaker: text` lines on save.
const EDITOR_STYLES = `
.te-doc { display: block; }
.te-doc:focus { outline: none; }
.te-bubble {
  position: relative;
  width: fit-content;
  max-width: 80%;
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  line-height: 1.625;
  white-space: pre-wrap;
  word-break: break-word;
}
.te-bubble[data-label]:not([data-label=""])::before {
  content: attr(data-label);
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
}
.te-bubble-us {
  margin-left: auto;
  margin-right: 0;
  background: #e0e7ff;
  color: #111827;
  border-bottom-right-radius: 0.125rem;
}
.te-bubble-them {
  margin-left: 0;
  margin-right: auto;
  background: #ffffff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 0.125rem;
}
.te-flip {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  opacity: 0.45;
  transition: opacity 0.1s;
}
.te-bubble:hover .te-flip,
.te-flip:hover {
  opacity: 1;
}
`;

const TranscriptModal: React.FC<TranscriptModalProps> = ({
  interactionId,
  transcript,
  canEdit: mayEdit,
  highlightText,
  highlightOccurrence,
  onClose,
  onSave,
  onDelete,
  onRenameSpeaker,
  onReassignSpeaker,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [copied, flashCopied] = useFlash(1500);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  // #11884: a suggestion taken or a name confirmed from the list, in flight,
  // and why the last one failed.
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // #12489: the speaker whose lines the reader is going through, and which
  // of their lines is current. The person picker is open beside the lines
  // for as long as a speaker is chosen.
  const pickerId = useId();
  const [chosen, setChosen] = useState<Chosen | null>(null);
  // #13917: a bubble clicked once, its header shown.
  const [revealed, setRevealed] = useState<StoredLine | null>(null);
  // Why the panel's last move was refused.
  const [moveError, setMoveError] = useState<string | null>(null);
  const [lineRefs] = useState(() => new Map<number, HTMLDivElement>());
  const canEdit = mayEdit && !!onSave && !!onDelete;
  const mayRename = canEdit && !!onRenameSpeaker;
  // #11884: what recognition could say about each speaker (who an unnamed
  // voice sounds like, which names it was unsure of), and whether the
  // organization has Speaker Identification on at all.
  const { data: speakerData } = useTranscriptSpeakers(
    interactionId,
    mayRename && !isEditing
  );
  // Nothing is offered until the speakers response says the organization
  // has the feature on; off, the transcript reads as before it existed.
  const canRename = mayRename && speakerData?.speaker_identification === true;
  // #14225: the transcript marked as correct, every unsure name at once.
  const confirmSpeakers = useConfirmTranscriptSpeakers();
  // #11884: the voices nobody has named yet, offered first so none is missed,
  // and the names to confirm.
  const { unnamed, toConfirm } = useMemo(() => {
    if (!canRename || isEditing) return { unnamed: [], toConfirm: [] };
    const hints = new Map(
      (speakerData?.speakers ?? []).map((s) => [s.label, s] as const)
    );
    return {
      unnamed: unidentifiedSpeakers(parseTranscript(transcript)).map((s) => ({
        ...s,
        suggestion: hints.get(s.label)?.suggestion ?? null,
      })),
      // The speaker heard most first (#14225): recognition's and the
      // model's names both wait here.
      toConfirm: (speakerData?.speakers ?? [])
        .filter((s) => s.unconfirmed)
        .sort((a, b) => b.lines - a.lines)
        .map((s) => ({
          label: s.label,
          lines: s.lines,
        })),
    };
  }, [canRename, isEditing, transcript, speakerData]);

  const lines = useMemo(() => parseTranscriptLines(transcript), [transcript]);
  const storedLines = useMemo(() => transcript.split("\n"), [transcript]);
  const linesOf = useCallback(
    (label: string): number[] =>
      lines.flatMap((line, idx) => (line.speaker === label ? [idx] : [])),
    [lines]
  );
  // Speakers are chosen only where they can be named.
  const picking = canRename && !isEditing && chosen ? chosen : null;
  const chosenLines = useMemo(
    () => (chosen ? linesOf(chosen.label) : []),
    [chosen, linesOf]
  );
  const currentLine = picking ? chosenLines[picking.at] : undefined;
  // #13917: a line is moved only where speakers can be named.
  const canMove = canRename && !!onReassignSpeaker;
  const storedAt = useCallback(
    (idx: number): StoredLine => ({
      line: lines[idx].line,
      raw: storedLines[lines[idx].line],
    }),
    [lines, storedLines]
  );
  // The panel's current line, and the run it sits in (consecutive lines by
  // its speaker); a move takes the run or the line alone.
  const moveAt = canMove && picking ? currentLine : undefined;
  const run = useMemo(
    () => (moveAt === undefined ? [] : runAround(lines, moveAt)),
    [lines, moveAt]
  );
  const moving =
    moveAt === undefined
      ? []
      : picking?.scope === "run" && run.length > 1
        ? run
        : [moveAt];
  // The revealed line's place among the bubbles, while the stored text
  // still holds it as it was clicked.
  const revealedIdx =
    canMove &&
    !isEditing &&
    revealed &&
    storedLines[revealed.line] === revealed.raw
      ? lines.findIndex((line) => line.line === revealed.line)
      : -1;
  const closePanel = () => {
    setChosen(null);
    setRevealed(null);
    setMoveError(null);
  };
  // The panel at one of a speaker's lines: from its name, its card or its
  // bubble. The move section starts on the line's run.
  const openAt = (label: string, idx: number) => {
    const theirs = linesOf(label);
    setChosen({
      label,
      at: Math.max(0, theirs.indexOf(idx)),
      scope: "run",
      anchor: idx >= 0 && idx < lines.length ? storedAt(idx) : undefined,
    });
    setMoveError(null);
  };
  // A speaker's card (their first line), or a name over one of their lines
  // (that line). Their own card again lets them go. A reveal elsewhere goes.
  const choose = (label: string, line?: number) => {
    if (line === undefined && chosen?.label === label) {
      closePanel();
      return;
    }
    const idx = line ?? linesOf(label)[0] ?? -1;
    openAt(label, idx);
    if (revealedIdx >= 0 && revealedIdx !== idx) setRevealed(null);
  };
  // A bubble is its line (#13917): the first click shows its header, a
  // second (or a double-click) opens the speaker panel at that line, as its
  // name does. A click that ends a drag selection of text is not a click.
  const linesRef = useRef<HTMLDivElement>(null);
  // Where the clicked bubble was on screen, so a header the click adds above
  // it does not move it (below).
  const pinned = useRef<{ bubble: HTMLElement; top: number } | null>(null);
  const clickBubble = (idx: number, bubble: HTMLElement) => {
    const text = window.getSelection();
    if (
      text &&
      !text.isCollapsed &&
      linesRef.current?.contains(text.anchorNode)
    ) {
      return;
    }
    if (revealedIdx === idx) {
      if (currentLine !== idx) openAt(lines[idx].speaker, idx);
      return;
    }
    pinned.current = { bubble, top: bubble.getBoundingClientRect().top };
    setRevealed(storedAt(idx));
  };
  // A reveal adds a header above the bubble (and may take one away from
  // the line revealed before). Before the frame is painted the lines scroll
  // by however far that moved the bubble, so it stays under the pointer and
  // a second click lands on it. That holds at the top and the bottom of a
  // transcript that scrolls (the header adds its own height to the scroll
  // range). One too short to scroll cannot: the modal grows and re-centres,
  // and the bubble moves down.
  useLayoutEffect(() => {
    const was = pinned.current;
    pinned.current = null;
    const scroller = linesRef.current;
    if (!was || !scroller || !was.bubble.isConnected) return;
    const moved = was.bubble.getBoundingClientRect().top - was.top;
    if (moved !== 0) scroller.scrollTop += moved;
  }, [revealed]);
  // A reveal goes when a click lands outside every bubble, the panel and
  // the navigator (the panel stays); a click on another bubble reveals that
  // one instead, and one on a speaker's name opens the panel (so the press
  // must not take the name away first). While the revealed line is the
  // panel's current line, its header stays with the panel.
  const revealing = revealedIdx >= 0 && revealedIdx !== currentLine;
  useEffect(() => {
    if (!revealing) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (
        !target?.closest(
          "[data-line-bubble], [data-line-header] button, [data-speaker-panel], [role='search']"
        )
      ) {
        setRevealed(null);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [revealing]);
  // Stepping goes to the speaker's next or previous line; the move section
  // goes with it, back on the run.
  const step = (by: 1 | -1) => {
    if (!chosen || chosenLines.length === 0) return;
    const at = (chosen.at + by + chosenLines.length) % chosenLines.length;
    setChosen({
      ...chosen,
      at,
      scope: "run",
      anchor: storedAt(chosenLines[at]),
      held: undefined,
      follow: undefined,
    });
    // A step clears any reveal: the panel goes to another line.
    setRevealed(null);
    setMoveError(null);
  };
  // A speaker whose lines are gone (renamed, or all moved) is let go; one
  // who lost some keeps a position that exists.
  useEffect(() => {
    if (!chosen || chosen.held || chosen.follow) return;
    if (chosenLines.length === 0) closePanel();
    else if (chosen.at >= chosenLines.length) {
      setChosen({ ...chosen, at: chosenLines.length - 1, anchor: undefined });
    }
  }, [chosen, chosenLines.length]);
  // #13917: the current line the transcript no longer holds as it was is
  // let go, and one that only moved within it is followed.
  useEffect(() => {
    if (!canMove || !chosen?.anchor || chosen.held || chosen.follow) return;
    const { line, raw } = chosen.anchor;
    if (storedLines[line] !== raw) {
      closePanel();
      return;
    }
    const at = chosenLines.indexOf(lines.findIndex((l) => l.line === line));
    if (at >= 0 && at !== chosen.at) setChosen({ ...chosen, at });
  }, [canMove, chosen, storedLines, lines, chosenLines]);
  // After a move: on the transcript that follows it, the panel goes on to
  // the line it named (the speaker's next, or the one before), found by its
  // text. After a move refused as stale, the message wins: the hold lasts
  // until the first transcript that differs from the one refused against,
  // and on it the line is found again by its text, nearest where it was (a
  // line dropped above moves it up a slot), under whichever speaker it now
  // has. Found, the panel stays there, and a run that changed is not the
  // run the reader picked, so the scope goes back to this line. Not found,
  // the panel is let go.
  useEffect(() => {
    if (!chosen) return;
    if (chosen.follow) {
      const { against, to } = chosen.follow;
      if (transcript === against) return;
      const at = to
        ? refindLine(storedLines, storedLineRef(to.raw, 0).text, to.line)
        : -1;
      const idx = at < 0 ? -1 : lines.findIndex((l) => l.line === at);
      // Under whichever speaker the line now carries: splitting a bare
      // "Them" letters the lines it leaves behind (#13917).
      const label = (idx >= 0 && lines[idx].speaker) || chosen.label;
      const pos = linesOf(label).indexOf(idx);
      setChosen(
        pos >= 0
          ? {
              ...chosen,
              label,
              follow: undefined,
              at: pos,
              anchor: { line: at, raw: storedLines[at] },
            }
          : { ...chosen, follow: undefined, anchor: undefined }
      );
      return;
    }
    if (!chosen.held || transcript === chosen.held.against) return;
    const held = chosen.held;
    const at = refindLine(
      storedLines,
      storedLineRef(held.raw, 0).text,
      held.line
    );
    const idx = at < 0 ? -1 : lines.findIndex((l) => l.line === at);
    if (idx < 0) {
      closePanel();
      return;
    }
    const label = lines[idx].speaker;
    const runNow = runAround(lines, idx).map((i) => storedLines[lines[i].line]);
    const sameRun =
      runNow.length === held.run.length &&
      runNow.every((raw, i) => raw === held.run[i]);
    setChosen({
      label,
      at: Math.max(0, linesOf(label).indexOf(idx)),
      scope: sameRun ? chosen.scope : "line",
      anchor: { line: at, raw: storedLines[at] },
    });
  }, [chosen, transcript, storedLines, lines, linesOf]);
  // An edit may drop the lines the panel points at: it ends the panel.
  useEffect(() => {
    if (isEditing) closePanel();
  }, [isEditing]);
  useEffect(() => {
    if (currentLine === undefined) return;
    const line = lineRefs.get(currentLine);
    if (!line) return;
    // Centred in the lines' box; a line taller than the box starts at its
    // top, so it reads from the beginning.
    const box = linesRef.current;
    const tall =
      !!box && line.getBoundingClientRect().height > box.clientHeight;
    line.scrollIntoView({
      behavior: "smooth",
      block: tall ? "start" : "center",
    });
  }, [currentLine, lineRefs]);
  const selecting = !!picking || revealedIdx >= 0;
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting]);
  // The transcript's speakers the current line can move to: every label but
  // its own, with how many lines each has.
  const otherSpeakers = useMemo(() => {
    if (!picking) return [];
    const counts = new Map<string, number>();
    for (const line of lines) {
      if (line.speaker && line.speaker !== picking.label) {
        counts.set(line.speaker, (counts.get(line.speaker) ?? 0) + 1);
      }
    }
    return Array.from(counts, ([label, count]) => ({ label, lines: count }));
  }, [lines, picking]);
  const moveLines = async (speaker: string | null) => {
    if (!onReassignSpeaker || !picking || moveAt === undefined) return;
    const label = picking.label;
    const against = transcript;
    const current = storedAt(moveAt);
    const runThen = run.map((idx) => storedLines[lines[idx].line]);
    // Where the panel goes on to once the move lands: the speaker's next
    // line after the moved ones, or the one before them.
    const lo = Math.min(...moving);
    const hi = Math.max(...moving);
    const next =
      chosenLines.find((i) => i > hi) ??
      [...chosenLines].reverse().find((i) => i < lo);
    setMoveError(null);
    // Set before the move goes, so the transcript it brings, however soon it
    // lands, is read as this move's and not as a change under the panel.
    setChosen((c) =>
      c?.label === label
        ? {
            ...c,
            scope: "run",
            held: undefined,
            follow: {
              against,
              to: next === undefined ? null : storedAt(next),
            },
          }
        : c
    );
    try {
      await onReassignSpeaker(
        moving.map((idx) => storedLineRef(transcript, lines[idx].line)),
        speaker
      );
    } catch (err) {
      // Refused as stale: the panel keeps saying why through the refetch.
      // Any other refusal shows its message and changes nothing.
      setMoveError(err instanceof Error ? err.message : String(err));
      setChosen((c) =>
        c?.label === label
          ? {
              ...c,
              scope: picking.scope,
              follow: undefined,
              held:
                err instanceof TranscriptChangedError
                  ? { ...current, against, run: runThen }
                  : undefined,
            }
          : c
      );
    }
  };
  // #13917: where speakers carry names the editor's ⇄ flip, which only
  // knows "Us" and "Them", would replace a name with a side; the speaker
  // panel outside the editor is the way to change a line's speaker there.
  // Decided once, when edit mode starts, from the speakers answer in hand:
  // until it has landed the flag counts as on (a flip on a named transcript
  // does damage, a missing one costs a moment), and a later answer never
  // re-seeds the editor. Without a rename handler there is no answer to
  // wait for, and the flip stays as it always was.
  const [bubbleOptions, setBubbleOptions] = useState<BubbleOptions>({
    flip: true,
  });
  const startEditing = () => {
    setBubbleOptions({
      flip: !mayRename || speakerData?.speaker_identification === false,
    });
    setIsEditing(true);
  };

  // #11884: transcript lines carrying the reader's own name sit on their
  // side of the conversation, like "Us" does.
  const { data: currentUser } = useCurrentUser();
  useEffect(() => {
    setHostSpeakerNames([currentUser?.name]);
  }, [currentUser?.name]);

  // Per-line wall-clock capture time (#10002). Fetched from the saved utterance
  // rows and matched to the parsed lines by index; only used when the counts
  // line up — an edited transcript can desync them, in which case we show none.
  const [createdAtByLine, setCreatedAtByLine] = useState<(string | null)[]>([]);
  // The channel each line was captured on, by the same index match. A line on
  // the recorder's mic is the reader's side whatever name it carries (#11884);
  // the stored label alone cannot say, since a name may be spelled from the
  // calendar or derived from an email rather than being the account name.
  const [sourceByLine, setSourceByLine] = useState<(string | null)[]>([]);
  // A room recording (one microphone hearing everyone, #11884): every row
  // came in on the mic, so the channel would put the whole room on the
  // reader's side. The recording's shape overrides the channel and places
  // every line on the far side, except the lines named as the reader.
  const [isRoom, setIsRoom] = useState(false);
  useEffect(() => {
    // A note viewed from its file alone has no interaction to ask.
    if (!interactionId) return;
    let cancelled = false;
    fetchTranscriptUtterances(interactionId)
      .then(({ utterances, captureShape }) => {
        if (cancelled) return;
        const lineCount = parseTranscript(transcript).length;
        const aligned = utterances.length === lineCount;
        setCreatedAtByLine(aligned ? utterances.map((u) => u.created_at) : []);
        setSourceByLine(aligned ? utterances.map((u) => u.source) : []);
        setIsRoom(captureShape === "room");
      })
      .catch(() => {
        if (!cancelled) {
          setCreatedAtByLine([]);
          setSourceByLine([]);
          setIsRoom(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [interactionId, transcript]);

  // Which side of the conversation a line sits on: its channel when the
  // rows line up, else its name. In a room the channel says nothing, but the
  // reader is there too: their named lines stay on their side.
  const isUsAt = (idx: number): boolean => {
    const source = sourceByLine[idx] ?? null;
    const speaker = lines[idx].speaker;
    return isRoom
      ? isHostSideSpeaker(speaker, [currentUser?.name])
      : source
        ? source === "mic"
        : isHostSideSpeaker(speaker, [currentUser?.name]);
  };

  // Seed the contentEditable with bubbles when entering edit mode. We set the
  // DOM directly (not React-controlled) so the caret isn't reset on every edit.
  // (The speakers query is off while editing, so the options hold still.)
  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.innerHTML = turnsToHtml(
        parseTranscript(transcript),
        bubbleOptions
      );
    }
  }, [isEditing, transcript, bubbleOptions]);

  const handleSave = async () => {
    if (!editorRef.current || !onSave) return;
    setIsSaving(true);
    try {
      await onSave(serializeEditor(editorRef.current));
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
      setConfirmingDelete(false);
    }
  };

  // Keep paste as plain text. A pasted chunk that is itself a transcript (e.g.
  // copied from another meeting) is re-bubbled by speaker and dropped in after
  // the caret's bubble; anything else is inserted as text into the current turn.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const container = editorRef.current;
    if (!container) return;
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;
    e.preventDefault();

    const { speakerLines, turns } = classifyPaste(text);
    const selection = window.getSelection();

    if (!speakerLines) {
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const node = document.createTextNode(text);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        container.appendChild(document.createTextNode(text));
      }
      return;
    }

    const template = document.createElement("template");
    template.innerHTML = turnsToHtml(turns, bubbleOptions);
    const fragment = template.content;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      let block: Node | null = range.startContainer;
      while (block && block.parentNode !== container) block = block.parentNode;
      if (block && block.parentNode === container) {
        container.insertBefore(fragment, block.nextSibling);
      } else {
        container.appendChild(fragment);
      }
    } else {
      container.appendChild(fragment);
    }
  };

  // Copy/cut a selection as the speaker-prefixed wire format so a highlighted
  // chunk pasted elsewhere (or back here) retains the us-vs-them attribution —
  // the on-screen "You"/"Them" labels are CSS pseudo-elements a raw copy loses.
  const handleClipboardOut = (
    e: React.ClipboardEvent<HTMLDivElement>,
    isCut: boolean
  ) => {
    const container = editorRef.current;
    if (!container) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0)
      return;
    const range = selection.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) return;
    const wire = serializeRange(range);
    if (!wire) return;
    e.preventDefault();
    e.clipboardData.setData("text/plain", wire);
    if (isCut) range.deleteContents();
  };

  // Clicking a bubble's toggle flips it between You and Them — the way to
  // re-attribute a turn or, after pressing Enter for a new line, author the
  // opposite speaker's bubble.
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const flip = (e.target as HTMLElement).closest("[data-flip]");
    if (!flip) return;
    e.preventDefault();
    const bubble = flip.closest("[data-speaker]");
    if (bubble) flipBubbleSpeaker(bubble as HTMLElement);
  };

  // A new line the browser creates on Enter starts as a bare block; normalize it
  // into a proper bubble (inherited speaker, styling, toggle) as the user types.
  const handleInput = () => {
    if (editorRef.current) normalizeBubbles(editorRef.current, bubbleOptions);
  };

  // Land on the cited passage once the lines have rendered (#11205).
  useEffect(() => {
    if (!highlightText) return;
    const hits = document.querySelectorAll("[data-transcript-hit]");
    const el = hits[highlightOccurrence ?? 0] ?? hits[0];
    if (el) el.scrollIntoView({ block: "center" });
  }, [highlightText, highlightOccurrence, transcript]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <style>{EDITOR_STYLES}</style>
      <div className="bg-white rounded-lg w-full md:w-3/4 max-w-4xl max-h-[90vh] flex flex-col mx-4">
        <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 flex-shrink-0">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900">
            Meeting Transcript
          </h3>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(transcript);
                    flashCopied();
                  }}
                  className="flex items-center gap-1.5 rounded-md bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied" : "Copy"}
                </button>
                {canEdit && (
                  <>
                    <button
                      onClick={startEditing}
                      className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(true)}
                      className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </>
                )}
              </>
            )}
            {isEditing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-md bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700 disabled:opacity-50"
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </>
            )}
            <Tooltip title="Close">
              <button
                aria-label="Close transcript"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </Tooltip>
          </div>
        </div>

        {confirmingDelete && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center justify-between gap-4">
            <span className="text-sm text-gray-800">
              Remove this transcript? The meeting notes, AI notes and summary
              are kept, but the AI notes can no longer be regenerated.
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="rounded-md bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700 disabled:opacity-50"
              >
                {isDeleting ? "Removing…" : "Remove transcript"}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={isDeleting}
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* #13917: on a phone the speaker panel and the cards would leave
            the lines no room, and the open panel already offers what a
            card does, so the cards stand down until it closes. */}
        <div
          className={
            moveAt !== undefined ? "contents max-md:hidden" : "contents"
          }
        >
          <UnidentifiedSpeakers
            speakers={unnamed}
            toConfirm={toConfirm}
            chosen={picking?.label ?? null}
            pickerId={pickerId}
            onIdentify={(label) => choose(label)}
            pending={assigning}
            error={assignError}
            onConfirmAll={async () => {
              if (assigning) return;
              setAssigning(true);
              setAssignError(null);
              try {
                await confirmSpeakers.mutateAsync(interactionId);
              } catch (err) {
                setAssignError(
                  userMessageOf(err, "The names could not be confirmed")
                );
              } finally {
                setAssigning(false);
              }
            }}
            onAssign={async (label, name, userId) => {
              if (assigning || !onRenameSpeaker) return;
              setAssigning(true);
              setAssignError(null);
              try {
                await onRenameSpeaker(label, name, userId);
              } catch (err) {
                setAssignError(
                  err instanceof Error ? err.message : String(err)
                );
              } finally {
                setAssigning(false);
              }
            }}
          />
        </div>
        {picking && (
          <SpeakerLineNavigator
            label={picking.label}
            at={picking.at}
            total={chosenLines.length}
            onStep={step}
            onClose={closePanel}
          />
        )}
        {/* The speaker panel sits beside the lines, never over them (#12489): a
            column at the right on a desktop, under the lines on a phone. */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div
            ref={linesRef}
            data-testid="transcript-lines"
            className="min-h-0 flex-1 overflow-y-auto p-6 bg-gray-50"
          >
            {!isEditing ? (
              <div className="max-w-3xl mx-auto">
                {lines.map((line, idx) => {
                  const isUs = isUsAt(idx);
                  const timestamp = formatTranscriptTimestamp(
                    createdAtByLine[idx] ?? null
                  );
                  // Consecutive lines from one speaker collapse into a group
                  // carrying a single header, Slack-style, instead of stamping a
                  // name and time onto every few seconds of speech (#11418).
                  const startsGroup = startsTranscriptGroup(
                    idx > 0
                      ? {
                          speaker: lines[idx - 1].speaker,
                          capturedAt: createdAtByLine[idx - 1] ?? null,
                        }
                      : null,
                    {
                      speaker: line.speaker,
                      capturedAt: createdAtByLine[idx] ?? null,
                    }
                  );
                  const isHit =
                    !!highlightText && line.text.includes(highlightText);
                  // #12489: the chosen speaker's current line is outlined,
                  // their other lines faintly. #13917: where the panel
                  // moves lines, the faint outline is the run a move takes.
                  // #13917: only a line with a speaker can be moved; an
                  // unlabelled line is inert.
                  const lineCanMove = canMove && !!line.speaker;
                  const isRevealed = idx === revealedIdx;
                  const startsHeader =
                    startsGroup && !!(line.speaker || timestamp);
                  // A revealed line gets the header a run's first line
                  // has, unless it is that first line.
                  const namesLine =
                    isRevealed && !!line.speaker && !startsHeader;
                  const isCurrent = idx === currentLine;
                  // The strong outline is the lines a move takes: the
                  // current line, or its whole run alike.
                  const isMoving = canMove ? moving.includes(idx) : isCurrent;
                  const isRelated =
                    !isMoving &&
                    !canMove &&
                    picking !== null &&
                    line.speaker === picking.label;
                  return (
                    <div
                      key={idx}
                      ref={(el) => {
                        if (el) lineRefs.set(idx, el);
                        else lineRefs.delete(idx);
                      }}
                      aria-current={isCurrent || undefined}
                      data-line={line.line}
                      data-outline={
                        isMoving ? "strong" : isRelated ? "faint" : undefined
                      }
                      data-transcript-hit={isHit || undefined}
                      className={`flex flex-col ${
                        isUs ? "items-end" : "items-start"
                      } ${startsGroup || namesLine ? "mt-3 first:mt-0" : "mt-1"} ${
                        isHit
                          ? "rounded-lg ring-2 ring-amber-300 bg-amber-50"
                          : ""
                      }`}
                    >
                      {/* The bubble plus a fixed gutter beside it. The gutter is
                        always reserved so bubble widths stay even down the
                        transcript, and it holds a collapsed line's own time,
                        revealed on hover. */}
                      <div
                        className={`group flex items-start gap-2 max-w-[80%] ${
                          isUs ? "flex-row-reverse" : "flex-row"
                        }`}
                      >
                        <div
                          className={`flex flex-col min-w-0 ${
                            isUs ? "items-end" : "items-start"
                          }`}
                        >
                          {/* Speaker label with the wall-clock time next to it
                            (#10002): "Them  3:04 PM" on the left, "3:04 PM  Us"
                            on the right. #13917: a revealed line in a run
                            gets the same header, name button and all: a
                            name is identification wherever it shows. */}
                          {(startsHeader || namesLine) && (
                            <div
                              data-line-header
                              className={`flex items-center gap-1.5 text-xs mb-1 px-1 ${
                                isUs ? "flex-row-reverse" : "flex-row"
                              }`}
                            >
                              {line.speaker &&
                                (canRename ? (
                                  <button
                                    type="button"
                                    aria-label={`Rename ${line.speaker}`}
                                    onClick={() => choose(line.speaker, idx)}
                                    className="cursor-pointer rounded text-gray-500 hover:text-gray-800 hover:underline"
                                  >
                                    {displaySpeaker(line.speaker)}
                                  </button>
                                ) : (
                                  <span className="text-gray-500">
                                    {displaySpeaker(line.speaker)}
                                  </span>
                                ))}
                              {timestamp && (
                                <span className="text-gray-400 tabular-nums">
                                  {timestamp}
                                </span>
                              )}
                            </div>
                          )}
                          <div
                            {...(lineCanMove
                              ? {
                                  role: "button",
                                  tabIndex: 0,
                                  // Its name is its own text; who said it
                                  // is the description.
                                  "aria-describedby": `${pickerId}-line-${idx}`,
                                  "aria-expanded": isRevealed,
                                  "aria-controls": isCurrent
                                    ? `${pickerId}-panel`
                                    : undefined,
                                  "data-line-bubble": true,
                                  onMouseDown: (e: React.MouseEvent) => {
                                    // A second click's default selects a
                                    // word; here it picks the line instead.
                                    if (e.detail > 1) e.preventDefault();
                                  },
                                  onClick: (e: React.MouseEvent<HTMLElement>) =>
                                    clickBubble(idx, e.currentTarget),
                                  onKeyDown: (
                                    e: React.KeyboardEvent<HTMLElement>
                                  ) => {
                                    if (e.key !== "Enter" && e.key !== " ")
                                      return;
                                    e.preventDefault();
                                    clickBubble(idx, e.currentTarget);
                                  },
                                }
                              : {})}
                            className={`rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                              lineCanMove
                                ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A3DCB]/50"
                                : ""
                            } ${
                              isUs
                                ? "bg-indigo-100 text-gray-900 rounded-br-sm"
                                : "bg-white text-gray-900 border border-gray-200 rounded-bl-sm"
                            } ${
                              isMoving
                                ? "ring-2 ring-[#2A3DCB]"
                                : isRelated
                                  ? "ring-1 ring-[#2A3DCB]/30"
                                  : ""
                            }`}
                          >
                            {line.text}
                          </div>
                          {lineCanMove && (
                            <span id={`${pickerId}-line-${idx}`} hidden>
                              {`Line ${idx + 1} by ${displaySpeaker(line.speaker)}`}
                            </span>
                          )}
                        </div>
                        <span
                          className={`w-14 shrink-0 pt-2.5 text-[10px] leading-none tabular-nums text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 ${
                            isUs ? "text-right" : "text-left"
                          }`}
                        >
                          {startsHeader || namesLine ? "" : timestamp}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                <p className="text-sm text-gray-500 mb-3">
                  Edit the transcript like a document — highlight across bubbles
                  to delete or copy a stretch, or paste a copied transcript to
                  append it (it keeps who said what).
                  {bubbleOptions.flip &&
                    " Hover a bubble and click ⇄ to switch the speaker."}{" "}
                  Saving with everything removed deletes the transcript.
                </p>
                <div
                  ref={editorRef}
                  role="textbox"
                  aria-multiline="true"
                  aria-label="Transcript editor"
                  data-testid="transcript-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onPaste={handlePaste}
                  onCopy={(e) => handleClipboardOut(e, false)}
                  onCut={(e) => handleClipboardOut(e, true)}
                  onClick={handleEditorClick}
                  onInput={handleInput}
                  className="te-doc min-h-[8rem] rounded-md border border-gray-200 bg-white p-4 focus:ring-1 focus:ring-indigo-300"
                />
              </div>
            )}
          </div>
          {picking && onRenameSpeaker && (
            // #12489, #13917: the speaker panel, beside the lines on a
            // desktop and docked under them on a phone, scrolling in its
            // own box: the current line's move, then who the speaker is.
            <div
              id={`${pickerId}-panel`}
              data-testid="speaker-panel"
              data-speaker-panel
              className="max-h-[40vh] flex-shrink-0 overflow-y-auto border-t border-gray-200 bg-white p-3 md:max-h-none md:w-72 md:border-l md:border-t-0 md:p-4"
            >
              {moveAt !== undefined && (
                <>
                  <section aria-label="This line">
                    <div className="mb-2 flex items-baseline justify-between gap-2">
                      <p className="text-xs text-[#6E7480]">This line</p>
                      <span className="font-mono text-[11px] tabular-nums text-[#6E7480]">
                        {`${picking.at + 1} of ${chosenLines.length}`}
                      </span>
                    </div>
                    <blockquote className="mb-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs leading-normal text-[#10141B]">
                      {/* Clamped inside the padding, so no third line shows
                          in it. */}
                      <span className="line-clamp-2">{lines[moveAt].text}</span>
                    </blockquote>
                    {run.length > 1 && (
                      <div
                        role="radiogroup"
                        aria-label="Lines to move"
                        // The design system's segmented control: a well, the
                        // chosen segment white in it; a ring only for the
                        // keyboard.
                        className="mb-2 grid grid-cols-2 gap-0.5 rounded-[11px] bg-[var(--fp-bg-well)] p-[4px] text-xs"
                      >
                        {(
                          [
                            ["line", "This line"],
                            ["run", `This run · ${run.length} lines`],
                          ] as const
                        ).map(([scope, label]) => {
                          const on = picking.scope === scope;
                          return (
                            <button
                              key={scope}
                              type="button"
                              role="radio"
                              aria-checked={on}
                              onClick={() =>
                                setChosen((c) => (c ? { ...c, scope } : c))
                              }
                              className={`cursor-pointer truncate rounded-[7px] px-2 py-1.5 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#2A3DCB]/50 ${
                                on
                                  ? "bg-[var(--fp-surface)] text-[var(--fp-ink)]"
                                  : "text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <SpeakerMovePicker
                      speakers={otherSpeakers}
                      newSpeaker={speakerData?.new_speaker_for?.[picking.label]}
                      onMove={moveLines}
                      error={moveError}
                    />
                  </section>
                  <hr className="my-3 border-gray-200" />
                </>
              )}
              <section id={pickerId} aria-label={`Who is ${picking.label}?`}>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <p className="truncate text-xs text-[#6E7480]">
                    Who is {displaySpeaker(picking.label)}?
                  </p>
                  {moveAt !== undefined && (
                    <span className="flex-shrink-0 font-mono text-[11px] text-[#6E7480]">
                      {chosenLines.length === 1
                        ? "1 line"
                        : `all ${chosenLines.length} lines`}
                    </span>
                  )}
                </div>
                <SpeakerRenamePicker
                  // A fresh field for each speaker chosen.
                  key={picking.label}
                  interactionId={interactionId}
                  onClose={closePanel}
                  onRename={(name, userId, entityId) =>
                    onRenameSpeaker(picking.label, name, userId, entityId)
                  }
                />
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptModal;
